import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { Type, PaintBucket, Eraser } from 'lucide-react';

interface EditableTextProps {
    value: string;
    onSave: (newValue: string) => void;
    mode: 'view' | 'edit';
    className?: string;
    multiline?: boolean;
    tag?: 'h1' | 'h2' | 'h3' | 'p' | 'span' | 'div';
    placeholder?: string;
    renderView?: (text: string) => React.ReactNode; // Custom renderer for view mode
}

/**
 * Parse inline markup: {[color]:[size]|text} → <span style="color:#hex; fontSize: size">text</span>
 */
const renderColoredText = (text: string): React.ReactNode[] => {
    if (!text) return [];

    let keyCounter = 0;

    const processTextNode = (nodeText: string) => {
        const lines = nodeText.split('\n');
        const nodes: React.ReactNode[] = [];
        lines.forEach((line, i) => {
            if (i > 0) nodes.push(<br key={`br-${keyCounter++}`} />);
            if (line) nodes.push(line);
        });
        return nodes;
    };

    const parseMarkup = (str: string): React.ReactNode[] => {
        const parts: React.ReactNode[] = [];
        let current = '';
        let i = 0;
        
        while (i < str.length) {
            if (str[i] === '{') {
                let depth = 1;
                let j = i + 1;
                while (j < str.length && depth > 0) {
                    if (str[j] === '{') depth++;
                    else if (str[j] === '}') depth--;
                    j++;
                }
                
                if (depth === 0) {
                    if (current) {
                        parts.push(...processTextNode(current));
                        current = '';
                    }
                    
                    const block = str.substring(i + 1, j - 1);
                    const pipeIndex = block.indexOf('|');
                    
                    if (pipeIndex > -1) {
                        const styleStr = block.substring(0, pipeIndex);
                        const contentStr = block.substring(pipeIndex + 1);
                        const styleParts = styleStr.split(':');
                        const color = styleParts[0] || undefined;
                        const fontSize = styleParts[1] || undefined;
                        
                        const sizeClass = fontSize ? `parsed-size-${fontSize.replace('.', '-')}` : '';
                        
                        parts.push(
                            // eslint-disable-next-line react/forbid-component-props
                            <span key={`span-${keyCounter++}`} className={sizeClass} style={{ color, fontSize }}>
                                {parseMarkup(contentStr)}
                            </span>
                        );
                    } else {
                        parts.push(...processTextNode('{' + block + '}'));
                    }
                    i = j;
                    continue;
                }
            }
            current += str[i];
            i++;
        }
        
        if (current) {
            parts.push(...processTextNode(current));
        }
        
        return parts;
    };

    return parseMarkup(text);
};

const PRESET_COLORS = ['#f59e0b', '#38bdf8', '#10b981', '#f43f5e', '#a855f7', '#ffffff', '#94a3b8'];
const PRESET_SIZES = ['0.5em', '0.7em', '0.85em', '1em', '1.25em', '1.5em', '2em', '3em', '4em', '5em'];

export const EditableText: React.FC<EditableTextProps> = ({
    value, onSave, mode, className, multiline = false, tag: Tag = 'div', placeholder, renderView
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [tempValue, setTempValue] = useState(value);
    const inputRef = useRef<any>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setTempValue(value);
    }, [value]);

    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus();
            // Automatically select all text if it's single line, or put cursor at end
        }
    }, [isEditing]);

    const handleBlur = (e: React.FocusEvent) => {
        // Allow focus to move to the toolbar (e.g. the <select>) without closing edit mode
        if (wrapperRef.current && wrapperRef.current.contains(e.relatedTarget as Node)) {
            return;
        }
        setIsEditing(false);
        if (tempValue !== value) {
            onSave(tempValue);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            setTempValue(value);
            setIsEditing(false);
        }
    };

    const applyFormat = (colorStr: string, sizeStr: string) => {
        const start = inputRef.current?.selectionStart;
        const end = inputRef.current?.selectionEnd;
        if (start !== undefined && end !== undefined && start !== end) {
            const selectedText = tempValue.substring(start, end);
            const before = tempValue.substring(0, start);
            const after = tempValue.substring(end);
            
            let formatStr = '';
            if (colorStr && sizeStr) formatStr = `${colorStr}:${sizeStr}`;
            else if (colorStr) formatStr = `${colorStr}`;
            else if (sizeStr) formatStr = `:${sizeStr}`;
            
            const newText = `${before}{${formatStr}|${selectedText}}${after}`;
            setTempValue(newText);
            
            setTimeout(() => {
                inputRef.current?.focus();
                inputRef.current?.setSelectionRange(start, start + formatStr.length + selectedText.length + 3);
            }, 0);
        } else {
            alert("Bitte markiere zuerst ein Wort oder einen Textabschnitt im Feld.");
            // Re-focus the input so we don't lose edit mode
            setTimeout(() => inputRef.current?.focus(), 10);
        }
    };

    const clearFormat = () => {
        const start = inputRef.current?.selectionStart;
        const end = inputRef.current?.selectionEnd;
        if (start !== undefined && end !== undefined && start !== end) {
            const selectedText = tempValue.substring(start, end);
            const before = tempValue.substring(0, start);
            const after = tempValue.substring(end);
            
            const cleaned = selectedText.replace(/\{[^|]+\|([^}]+)\}/g, '$1');
            setTempValue(`${before}${cleaned}${after}`);
            
            setTimeout(() => {
                inputRef.current?.focus();
                inputRef.current?.setSelectionRange(start, start + cleaned.length);
            }, 0);
        } else {
            if (window.confirm("Formatierung im gesamten Text entfernen?")) {
                setTempValue(tempValue.replace(/\{[^|]+\|([^}]+)\}/g, '$1'));
                setTimeout(() => inputRef.current?.focus(), 10);
            } else {
                setTimeout(() => inputRef.current?.focus(), 10);
            }
        }
    };

    if (mode === 'view') {
        return <Tag className={className}>{renderView ? renderView(value) : (renderColoredText(value) || placeholder)}</Tag>;
    }

    if (isEditing) {
        // By adding inline-block and w-full, we allow it to inherit the parent's width constraints or expand naturally
        // while avoiding collapsing to 0. We also copy className layout properties loosely.
        return (
            <div ref={wrapperRef} className={classNames(className, "relative w-full !bg-transparent !border-none !p-0 !m-0 !ring-0 z-50")} style={{ minWidth: '300px' }}>
                {/* Floating Toolbar */}
                <div 
                    className="absolute bottom-full mb-2 left-0 bg-slate-900 border border-slate-700 rounded-lg p-2 flex flex-wrap items-center gap-3 shadow-2xl animate-in fade-in slide-in-from-bottom-2"
                >
                    <div className="flex items-center gap-1.5 border-r border-slate-700 pr-3">
                        <PaintBucket className="w-3.5 h-3.5 text-slate-400" />
                        {PRESET_COLORS.map(c => (
                            <button 
                                key={c}
                                type="button"
                                onMouseDown={(e) => { e.preventDefault(); applyFormat(c, ''); }}
                                className="w-5 h-5 rounded-full border border-slate-600 hover:scale-110 transition-transform shadow-inner"
                                style={{ backgroundColor: c }}
                                title={`Farbe: ${c}`}
                            />
                        ))}
                    </div>
                    
                    <div className="flex items-center gap-1.5 border-r border-slate-700 pr-3">
                        <Type className="w-3.5 h-3.5 text-slate-400" />
                        <select 
                            className="bg-slate-800 text-xs text-white border border-slate-600 rounded px-1 py-0.5 outline-none hover:border-sky-500 cursor-pointer"
                            onChange={(e) => { applyFormat('', e.target.value); e.target.value = ''; }}
                            defaultValue=""
                            title="Schriftgrösse"
                        >
                            <option value="" disabled>Grösse wählen...</option>
                            {PRESET_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </div>

                    <button 
                        type="button"
                        onMouseDown={(e) => { e.preventDefault(); clearFormat(); }}
                        className="flex items-center gap-1 text-xs text-slate-400 hover:text-rose-400 px-2 py-1 rounded bg-slate-800 hover:bg-slate-800/80 transition-colors"
                        title="Formatierung entfernen"
                    >
                        <Eraser className="w-3 h-3" />
                        Clear
                    </button>
                </div>

                {multiline ? (
                    <textarea
                        ref={inputRef}
                        value={tempValue}
                        onChange={(e) => setTempValue(e.target.value)}
                        onBlur={handleBlur}
                        onKeyDown={handleKeyDown}
                        className="bg-slate-800/95 outline-none border-2 border-sky-500 rounded p-4 w-full resize-y min-h-[120px] text-white shadow-xl ring-4 ring-sky-500/20 text-lg leading-relaxed"
                        title="Text bearbeiten – Nutze die Toolbar für Formatierungen"
                    />
                ) : (
                    <div className="relative inline-grid items-center w-full max-w-[90vw]">
                        {/* Hidden span forces parent width perfectly to match text */}
                        <span className="invisible whitespace-pre-wrap col-start-1 row-start-1 px-4 py-2 text-lg" style={{ minWidth: '300px' }}>
                            {tempValue || ' '}
                        </span>
                        <textarea
                            ref={inputRef}
                            value={tempValue}
                            onChange={(e) => setTempValue(e.target.value)}
                            onBlur={handleBlur}
                            onKeyDown={handleKeyDown}
                            className="col-start-1 row-start-1 bg-slate-800/95 outline-none border-2 border-sky-500 rounded px-4 py-2 w-full h-full text-white shadow-xl ring-4 ring-sky-500/20 text-lg overflow-hidden resize-none"
                            title="Text bearbeiten – Nutze die Toolbar für Formatierungen"
                            rows={1}
                        />
                    </div>
                )}
            </div>
        );
    }

    return (
        <Tag
            className={classNames(className, "cursor-text border border-transparent hover:border-sky-500/50 hover:bg-white/5 rounded transition-all relative group")}
            onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsEditing(true);
            }}
            title="Klicken zum Bearbeiten (Mit Formatierungs-Toolbar)"
        >
            {value ? (renderView ? renderView(value) : renderColoredText(value)) : <span className="opacity-50 italic">{placeholder || 'Leer...'}</span>}
            <span className="absolute -top-3 -right-3 hidden group-hover:flex items-center justify-center bg-sky-500 text-white text-[9px] px-1.5 py-0.5 rounded shadow-lg font-bold tracking-wider uppercase">Edit</span>
        </Tag>
    );
};
